import React, { useMemo, useState, useEffect, useRef, useCallback, useContext } from "react";
import { Box, Typography, Button, CircularProgress, Divider, Grid, Paper, Stack, Avatar, Chip, Tooltip, IconButton } from "@mui/material";
import { green } from "@mui/material/colors";
import CallIcon from "@mui/icons-material/Call";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { statusUpdate } from "../../../../service/Invoice/invoice";
import { notifyError, notifySuccess } from "../../../../components/ToastContainer/Toast/Toast";
import { getInvoiceList } from "../../../../service/Invoice/invoice";
import { getPreparingOrders, initiateExotelCall } from "../../../../service/order/orderService";
import { useParams } from "react-router-dom";
import vegIcon from "../../../../Assets/Veg_symbol.svg.png"
import nonVegIcon from "../../../../Assets/Non_veg_symbol.svg.png"
import eggIcon from "../../../../Assets/Egg.svg"
import ProgressButton from "../../../../components/ProgressButton/ProgressButton";
import { parseISODuration } from "./Utils/reusbleFunction";
import { OrderCountsContext, useOrderCounts } from "../../Tabs/InvoiceTabs";
import { useAppSelector } from "../../../../Redux/hooks";
import { useAppDispatch } from "../../../../Redux/hooks";
import { setActiveKey } from "../../../../Redux/Slices/tabSlice";
import CallInitiatedModal from "../../../../components/CallInitiatedModal/CallInitiatedModal";
import CopyToClipboardIcon from "../../../../components/Copy/CopyToClipboardIcon";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import PrintOrderModal from "./PrintOrderModal";
import {
  OrderPickupDeliveryCodes,
  resolvePickupDeliveryCodes,
} from "../OrderPickupDeliveryCodes";

// Food type icon component
const FoodTypeIcon = ({ productType }: { productType: string }) => {
    switch (productType) {
        case 'veg':
            return <img src={vegIcon} alt="Veg" style={{ width: 16, height: 16, marginRight: 8 }} />;
        case 'non-veg':
            return <img src={nonVegIcon} alt="Non-Veg" style={{ width: 16, height: 16, marginRight: 8 }} />;
        case 'egg':
            return <img src={eggIcon} alt="Egg" style={{ width: 16, height: 16, marginRight: 8 }} />;
        default:
            return null;
    }
};
const isTazty = String(process.env.REACT_APP_ORG_CODE ?? "").trim().toLowerCase() === "tazty";

// Helper function to calculate time ago string (re-used from New component)
const getTimeAgo = (dateString: string | Date): string => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) {
        return Math.floor(interval) + " years ago";
    }
    interval = seconds / 2592000;
    if (interval > 1) {
        return Math.floor(interval) + " months ago";
    }
    interval = seconds / 86400;
    if (interval > 1) {
        return Math.floor(interval) + " days ago";
    }
    interval = seconds / 3600;
    if (interval > 1) {
        return Math.floor(interval) + " hours ago";
    }
    interval = seconds / 60;
    if (interval > 1) {
        return Math.floor(interval) + " minutes ago";
    }
    return Math.floor(seconds) + " seconds ago";
};

// Sub-component for a single preparing order box
interface PreparingOrderBoxProps {
    order: any; // Define a more specific type if you have an Order interface
    onOrderReady: (orderId: string) => void;
    fetchInvoiceData: () => void;
}

const SinglePreparingOrderBox: React.FC<PreparingOrderBoxProps> = ({ order, onOrderReady, fetchInvoiceData }) => {
    const [timeAgoString, setTimeAgoString] = useState("N/A");
    const [calling, setCalling] = useState(false);
    const [callingAgent, setCallingAgent] = useState(false);
    const [showCallInitiatedModal, setShowCallInitiatedModal] = useState(false);
  const [showAgentCallInitiatedModal, setShowAgentCallInitiatedModal] =
    useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);

  const roleID = useAppSelector((state: any) => state.roleAndPermission?.roleID);
  const isSuperAdmin = Number(roleID) === 1;
  const isTazty = String(process.env.REACT_APP_ORG_CODE ?? "").trim().toLowerCase() === "tazty";
    
    // Get profile phone_number from Redux
  const profilePhoneNumber = useAppSelector(
    (state) => state.roleAndPermission?.phone_number || "",
  );

  // Get current store details from Redux for print receipt
  const { store_id } = useParams();
  const allStores = useAppSelector(
    (state: any) => state.roleAndPermission?.profileStoreId || [],
  );
  const currentStoreInfo = useMemo(() => {
    if (!store_id || !allStores.length) return null;
    return allStores.find((s: any) => String(s.id) === String(store_id)) || null;
  }, [store_id, allStores]);

    const currentOrder = useMemo(() => {
        const orderDateTime = new Date(order.updated_at || order.order_date);
        console.log("order", order);
        const addressParts = [
            order.customer?.billing_address?.address1,
            order.customer?.billing_address?.address2,
            order.customer?.billing_address?.address3,
            order.customer?.billing_address?.city,
            order.customer?.billing_address?.state,
      order.customer?.billing_address?.pincode,
        ].filter(Boolean);

        // Check if agent_details exists and is not null/undefined/empty
    const agentDetails =
      order.agent_details &&
            (order.agent_details.id || order.agent_details.name) 
            ? order.agent_details 
            : null;

        return {
            id: order.id,
            order_code: order.order_code,
            time: orderDateTime.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }),
            fullOrderDate: orderDateTime,
            customerName: order.customer?.name || 'N/A',
            customerPhone: order.customer?.phone || 'N/A',
            addressLines: [
                order.customer?.billing_address?.address1,
                order.customer?.billing_address?.address2,
                order.customer?.billing_address?.address3
            ].filter(Boolean).join(', '),
            cityStatePincode: [
                order.customer?.billing_address?.city,
                order.customer?.billing_address?.state,
                order.customer?.billing_address?.pincode
            ].filter(Boolean).join(', '),
            totalAmount: parseFloat(order.total_amount).toFixed(2),
            paymentStatus: order.payment_method === 'online' ? 'Paid' : 'Not Paid',
            instruction: order.instructions || order.instruction || 'No special instructions',
            cutleryInstruction: order.cutlery_instruction || null, // Assuming a field for this
            items: order.order_items || [],
            agentDetails: agentDetails ? {
                id: agentDetails.id || 'N/A',
                name: agentDetails.name || 'Delivery Partner',
                phone: agentDetails.phone || agentDetails.phone_number || 'N/A',
                image: agentDetails.image || null,
                status_history: agentDetails.status_history || []
            } : null,
            // Preserve order status for status badge display
            orderStatus: (order.status || order.invoice_status || order.order_status || 'billed').toString(),
            // You can add more derived properties as needed
            customerOrderCount: order.customer?.order_count || 'N/A', // Example: '10th Order'
            deliveryTimeEstimate: order.delivery_time_estimate || 'N/A', // Example: '1 Min, 7 Mins Away'
            shippingCharge: order.shipping_charge ? parseFloat(order.shipping_charge).toFixed(2) : undefined,
            cgst: order.cgst ? parseFloat(order.cgst).toFixed(2) : undefined,
            sgst: order.sgst ? parseFloat(order.sgst).toFixed(2) : undefined,
            igst: order.igst ? parseFloat(order.igst).toFixed(2) : undefined,
      preparationTime: order.preparation_time || "N/A",
      sybTotalAmount: order.sybTotalAmount
        ? parseFloat(order.sybTotalAmount).toFixed(2)
        : undefined,
      paymentMethod: order.payment_method || "",
      // Store details for print receipt — prefer live invoice store data, fallback to Redux
      storeName:
        order.store?.name ||
        currentStoreInfo?.name ||
        "",
      storeAddressLine:
        [
          order.store?.address?.address_line_1,
          order.store?.address?.address_line_2,
          order.store?.address?.address_line_3,
        ]
          .filter(Boolean)
          .join(", ") ||
        currentStoreInfo?.address3 ||
        "",
      storeCityState:
        [order.store?.address?.city, order.store?.address?.state]
          .filter(Boolean)
          .join(", ") ||
        currentStoreInfo?.city ||
        "",
      storePhone: order.store?.address?.phone_number || "",
      instructions: order.instructions || order.instruction || "",

        };
  }, [order, currentStoreInfo]);

  const { pickupCode, deliveryCode } = useMemo(
    () => resolvePickupDeliveryCodes(order),
    [order],
  );

    // const currentOrder = useMemo(() => {
    //     const orderDateTime = new Date(order.updated_at || order.order_date);

    //     const deliveryPartner = order.delivery_partner || {};
    //     return {
    //         ...order,
    //         fullOrderDate: orderDateTime,
    //         time: orderDateTime.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }),
    //         totalAmount: parseFloat(order.total_amount).toFixed(2),
    //         paymentStatus: order.payment_method === 'online' ? 'Paid' : 'Pending',
    //         items: order.order_items || []
    //     };
    // }, [order]);

    // 🔹 Group items + addons here
    // Local ISO8601 duration parser (supports PT#H#M#S)

    const [now, setNow] = useState(Date.now());
    const prepDuration = useMemo(() => parseISODuration(order.preparation_time), [order.preparation_time]);

    const orderPlaced = new Date(order.order_date || order.created_at || order.updated_at).getTime();
    const expiryTimestamp = orderPlaced + prepDuration * 1000;
    const remainingSeconds = Math.max(0, Math.floor((expiryTimestamp - now) / 1000));
    // Timer effect (updates every second)
    useEffect(() => {
        if (remainingSeconds > 0) {
            const interval = setInterval(() => setNow(Date.now()), 1000);
            return () => clearInterval(interval);
        }
    }, [expiryTimestamp, remainingSeconds]);
    // Expired if remainingSeconds === 0
    const isExpired = remainingSeconds === 0;
    // Format countdown as MM:SS
    const formatCountdown = (secs: number) => {
        const m = Math.floor(secs / 60).toString().padStart(2, '0');
        const s = (secs % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const groupedDisplayItems = useMemo(() => {
        const baseProductsMap = new Map<string, any>();

        currentOrder.items.forEach((item: any) => {
            if (String(item.product_id).startsWith("a-")) {
                // Addon
                const match = String(item.product_id).match(/a-(\d+)-(\d+)/);
                if (match && match[1]) {
                    const baseId = match[1];
                    const base = currentOrder.items.find((bp: any) => String(bp.product_id) === baseId);
                    if (base) {
                        if (!baseProductsMap.has(base.product_id)) {
                            baseProductsMap.set(base.product_id, { ...base, addons: [] });
                        }
                        baseProductsMap.get(base.product_id)?.addons.push(item);
                    } else {
                        baseProductsMap.set(item.product_id, { ...item, addons: [] });
                    }
                } else {
                    baseProductsMap.set(item.product_id, { ...item, addons: [] });
                }
            } else {
                if (!baseProductsMap.has(item.product_id)) {
                    baseProductsMap.set(item.product_id, { ...item, addons: [] });
                }
            }
        });
        return Array.from(baseProductsMap.values());
    }, [currentOrder.items]);
    const totalSeconds = useMemo(() => prepDuration, [prepDuration]);
    useEffect(() => {
        if (currentOrder.fullOrderDate) {
            const updateTimeAgo = () => {
                setTimeAgoString(getTimeAgo(currentOrder.fullOrderDate));
            };

            updateTimeAgo();
            const intervalId = setInterval(updateTimeAgo, 60 * 1000); // Update every minute

            return () => clearInterval(intervalId); // Cleanup interval on component unmount
        } else {
            setTimeAgoString("N/A");
        }
    }, [currentOrder.fullOrderDate]);

    const { moveOrderCount, refreshCallbacksRef } = useOrderCounts();
    const dispatch = useAppDispatch();

    // Format status for display (capitalize first letter, replace underscores/hyphens with spaces)
    const formatStatus = (status: string) => {
        if (!status) return 'Assigned';
        // Replace underscores and hyphens with spaces
        const withSpaces = status.replace(/[_-]/g, ' ');
        // Capitalize first letter of each word
        return withSpaces
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Format status with line break for "Agent Arrived Restaurant"
    const formatStatusWithLineBreak = (status: string) => {
        const formatted = formatStatus(status);
        // For "Agent Arrived Restaurant", split exactly at "Restaurant" to show:
        // Line 1: "Agent Arrived"
        // Line 2: "Restaurant"
        if (formatted.toLowerCase().includes('agent arrived restaurant')) {
            return 'Agent Arrived\nRestaurant';
        }
        return formatted;
    };

    const handleOrderReadyClick = async () => {
        try {
            const payload = { status: "packed" };
            const res = await statusUpdate(currentOrder.id, payload);
            if (res?.status === 200) {
                notifySuccess(`Order ${currentOrder.id} marked as Ready!`);

                // Update counts: move from 'preparing' to 'ready'
                moveOrderCount('preparing', 'ready');

                fetchInvoiceData();

                // Switch UI to In Progress tab ("ready") and refresh it so this order appears there.
                dispatch(setActiveKey("ready"));
                window.setTimeout(() => {
                  refreshCallbacksRef.current.ready?.();
                }, 400);
            } else {
                notifyError(res?.data?.message || "Failed to update order status.");
            }
        } catch (error: any) {
            notifyError(error?.response?.data?.message || "Something went wrong while updating order status.");
        }
    };

    const phone = currentOrder?.customerPhone;

    const handleCallClick = async () => {
        if (!phone || phone === "N/A" || !profilePhoneNumber) {
            notifyError("Phone number not available");
            return;
        }

        if (!currentOrder.order_code) {
            notifyError("Order code not available");
            return;
        }

        setCalling(true);
        try {
            const payload = {
                order_id: currentOrder.order_code,
                from: profilePhoneNumber,
                to: phone,
            };
            const response = await initiateExotelCall(payload);
            if (response?.status === 200 || response?.status === 201) {
                setShowCallInitiatedModal(true);
            } else {
                const errorMessage = response?.data?.message || "Failed to initiate call";
                notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || "Failed to initiate call";
            notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
        } finally {
            setCalling(false);
        }
    };

    return (
        <>
            <Paper
                sx={{
                    border: isExpired ? '1px solid red' : '1px solid #ddd',
                    display: "flex",
                    borderRadius: 1,
                    // border: "1px solid #ddd",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        bgcolor: "#f9f9f9",
                        flex: "0 0 35%",
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        borderRight: "1px dashed #ccc",
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: "#f9f9f9",
                            flex: "0 0 35%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            gap: 1.5,
                        }}
                    >
                        <Box>
                            <Box sx={{ mb: 0.5 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Order ID
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        {currentOrder.time}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ bgcolor: '#E0F2F1', px: 1.5, py: 0.5, borderRadius: '6px', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                        <Typography fontWeight={600} fontSize={12}>
                                            {currentOrder.order_code}
                                        </Typography>
                                    </Box>
                                    <CopyToClipboardIcon value={currentOrder.order_code || ""} title="Copy Order ID" size={16} />
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: 10, textTransform: 'uppercase', mb: 0.25, display: 'block', letterSpacing: 0.5 }}>
                                    Invoice ID
                                </Typography>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ bgcolor: '#E0F2F1', px: 1.5, py: 0.5, borderRadius: '6px', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                        <Typography fontWeight={600} fontSize={12}>
                                            {currentOrder.id}
                                        </Typography>
                                    </Box>
                                    <CopyToClipboardIcon value={String(currentOrder.id)} title="Copy Invoice ID" size={16} />
                                </Box>
                            </Box>
                        </Box>
                        {isTazty && (
                            <OrderPickupDeliveryCodes
                              tab="preparing"
                              pickupCode={pickupCode}
                              deliveryCode={deliveryCode}
                            />
                        )}
                        <Divider />

                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="body2" fontWeight="bold" sx={{ pt: 0.5, pb: 0.5 }}>
                                        {currentOrder.customerName}
                                    </Typography>
                                    {/* <Typography fontSize={12} fontWeight={500} sx={{ pt: 0.5 }}>
                                        {currentOrder.customerOrderCount} Order
                                    </Typography> */}
                                </Box>

                                <Box
                                    display="flex"
                                    alignItems="center"
                                    sx={{
                                        color: green[600],
                                        cursor: phone !== "N/A" && profilePhoneNumber ? "pointer" : "default",
                                        overflow: "hidden",
                                        width: "fit-content",
                                    }}
                                    onClick={handleCallClick}
                                >
                                    <CallIcon 
                                        fontSize="small" 
                                        sx={{ 
                                            mr: 0.5, 
                                            transition: "transform 0.3s",
                                            opacity: calling ? 0.6 : 1,
                                            cursor: phone !== "N/A" && profilePhoneNumber ? "pointer" : "default"
                                        }} 
                                    />
                                </Box>
                            </Box>

                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mt={0.5}
                            >
                                <Box sx={{ maxWidth: '70%' }}>
                                    <Typography
                                        fontSize={12}
                                        fontWeight={500}
                                        sx={{ display: 'block' }}
                                    >
                                        {currentOrder.addressLines}
                                    </Typography>
                                    <Typography
                                        fontSize={12}
                                        fontWeight={500}
                                        color="text.secondary"
                                        sx={{ display: 'block' }}
                                    >
                                        {currentOrder.cityStatePincode}
                                    </Typography>
                                </Box>
                                {/* <Typography fontSize={12} color="grey" sx={{ whiteSpace: 'nowrap' }}>
                                {currentOrder.deliveryTimeEstimate}
                            </Typography> */}
                            </Box>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography fontSize={12} fontWeight={500} color="grey" >
                                Instruction:
                            </Typography>
                            <Typography fontSize={12} fontWeight={500}>
                                {currentOrder.instruction}
                            </Typography>
                        </Box>

                        <Typography
                            variant="caption"
                            fontWeight={500}
                            display={"flex"}
                            justifyContent={"flex-end"}
                        >
                            Placed {timeAgoString}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: "0 0 68%",
                        display: "flex",
                        flexDirection: "row",
                        backgroundColor: "white",
                    }}
                >
                    {/* Items & buttons */}
                    <Box
                        sx={{
                            flex: "0 0 60.5%",
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            borderRight: "1px dashed #ccc",
                            gap: 2,
                        }}
                    >
                        {/* Don't send cutlery chip */}
                        {currentOrder.cutleryInstruction && (
                            <Chip
                                label={currentOrder.cutleryInstruction}
                                sx={{
                                    borderColor: "#d4af37",
                                    color: "#d4af37",
                                    backgroundColor: "transparent",
                                    border: "1px solid",
                                    borderRadius: "20px",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    alignSelf: "flex-start",
                                    height: 24,
                                }}
                            />
                        )}

                        {/* Items list */}
                        <Stack spacing={1}>
                            {groupedDisplayItems.map((item: any, i: number) => (
                                <Box key={item.product_id || i} // Use product_id for key, fallback to index
                                    display="flex"
                                    flexDirection="column" // Changed to column for stacking name+qty and addons
                                    alignItems="flex-start"
                                    mb={1}>
                                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {item.product_type === "veg" && (
                                                <img src={vegIcon} alt="Veg" style={{ width: 16, height: 16 }} />
                                            )}
                                            {item.product_type === "non-veg" && (
                                                <img src={nonVegIcon} alt="Non-Veg" style={{ width: 16, height: 16 }} />
                                            )}
                                            {item.product_type === "egg" && (
                                                <img src={eggIcon} alt="Egg" style={{ width: 16, height: 16 }} />
                                            )}
                                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                                {item.product_name} x {item.quantity}
                                            </Typography>
                                        </Box>
                                        <Typography fontWeight={500} sx={{ marginRight: 2 }}>₹{Number(item?.total_price || 0).toFixed(2)}</Typography>
                                    </Box>
                                    {/* Addons under base product */}
                                    {item.addons?.length > 0 && (
                                        <Box mt={.5} width="100%">
                                            {/* <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                        Add ons :
                                                    </Typography> */}
                                            {item.addons.map((addon: any, idx: number) => {
                                                return (
                                                    <Box key={idx} display="flex" justifyContent="space-between" width="100%" sx={{ pl: 2 }}>
                                                        <Typography key={idx} variant="caption" color="text.secondary" display="block" sx={{ pl: 1 }}>
                                                            • {addon.product_name} x {addon.quantity}
                                                        </Typography>
                                                        <Typography key={idx} variant="caption" color="text.secondary" display="block" sx={{ pl: 1, marginRight: 2 }}>
                                                            ₹{Number(addon.total_price).toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Stack>

                        <Divider />

                        <Box display="flex" flexDirection="column" alignItems="flex-start" mb={1}>
                            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">  Sub Total: </Typography>
                                <Typography variant="body2" fontSize={12} fontWeight={600} marginRight={2} color="grey">
                                    ₹{currentOrder.sybTotalAmount}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider />

                        <Box
                            sx={{
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                p: 2,
                                // mt: 2,
                            }}
                        >
                            <Grid container>
                                {/* Left side labels */}
                                <Grid item xs={9}>
                                    {/* <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        Delivery Charge
                                    </Typography> */}

                                    {Number(currentOrder?.cgst) !== 0 && (
                                        <>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                CGST
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                SGST
                                            </Typography>
                                        </>
                                    )}
                                </Grid>

                                {/* Right side values */}
                                <Grid item xs={3} textAlign="right">
                                    {/* <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        ₹{Number(currentOrder?.shippingCharge || 0).toFixed(2)}
                                    </Typography> */}

                                    {Number(currentOrder?.cgst) !== 0 && (
                                        <>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                ₹{Number(currentOrder?.cgst || 0).toFixed(2)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                ₹{Number(currentOrder?.sgst || 0).toFixed(2)}
                                            </Typography>
                                        </>
                                    )}
                                </Grid>
                            </Grid>

                            {/* Divider */}
                            <Box sx={{ borderTop: "1px dashed #e0e0e0", my: 1 }} />
                            {/* Total row */}
                            <Grid container>
                                <Grid item xs={9} display="flex" alignItems="center">
                                    <Typography fontWeight="bold" mr={1}>
                                        Total
                                    </Typography>
                                    <Box
                                        sx={{
                                            bgcolor: currentOrder.paymentStatus === "Paid" ? "green" : "orange",
                                            color: "white",
                                            fontSize: "0.7rem",
                                            px: 1,
                                            py: 0.2,
                                            borderRadius: "6px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {currentOrder.paymentStatus}
                                    </Box>
                                </Grid>
                                <Grid item xs={3} textAlign="right">
                                    <Typography fontWeight={600}>₹{currentOrder.totalAmount}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                mt: 2,
                gap: 1,
              }}
            >
              {isTazty && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setPrintModalOpen(true)}
                  startIcon={<LocalPrintshopOutlinedIcon fontSize="small" />}
                  sx={{
                    bgcolor: "white",
                    textTransform: "none",
                    fontWeight: 500,
                    height: 40,
                  }}
                >
                  Print Order
                </Button>
              )}
              <Box sx={{ width: "100%", display: "flex" }}>
                        <ProgressButton
                            totalSeconds={totalSeconds}
                            remainingSeconds={remainingSeconds}
                            label="Order Ready"
                            isExpired={isExpired}
                            onClick={handleOrderReadyClick}
                            formatCountdown={formatCountdown}
                  sx={{ mt: 0, height: 40, width: "100%" }}
                        />
              </Box>
            </Box>

                        {/* Red message if expired */}
                        {isExpired && (
                            <Box mt={0} textAlign="center">
                <Typography
                  sx={{ fontSize: 12, color: "red", fontWeight: 600 }}
                >
                                    Order is ready now.
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Delivery partner details */}
                    <Box
                        sx={{
                            flex: "0 0 35%",
                            p: 1,
                            alignItems: "center",
                            justifyContent: "center",
                            display: "flex",
                            minHeight: 200,
              backgroundColor: "#f9f9f9",
                        }}
                    >
                        {currentOrder?.agentDetails ? (
                            <Box
                                sx={{
                                    width: "100%", // full width of column
                                    display: "flex",
                                    justifyContent: "center", // center card horizontally
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between", // avatar + info + button
                                        width: "100%",
                                        maxWidth: 320, // optional, limits card width
                                        p: 2,
                                        bgcolor: "#fff",
                                        borderRadius: 1,
                                        border: "1px solid #e0e0e0",
                                        gap: 2,
                                    }}
                                >
                                    {/* Avatar */}
                                    <Avatar
                                        src={currentOrder.agentDetails.image || undefined}
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            fontSize: 18,
                                            bgcolor: "94A0b8",
                      borderRadius: 1,
                                        }}
                                    >
                                        {currentOrder.agentDetails.image
                                            ? null
                                            : currentOrder.agentDetails.name
                                                ? currentOrder.agentDetails.name.charAt(0).toUpperCase()
                        : "D"}
                                    </Avatar>

                                    {/* Info */}
                                    <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {currentOrder.agentDetails.name}
                                        </Typography>
                                        {/* {currentOrder.agentDetails.phone && currentOrder.agentDetails.phone !== 'N/A' && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {currentOrder.agentDetails.phone}
                                            </Typography>
                                        )} */}
                                        <Box sx={{ display: "flex", alignItems: currentOrder.orderStatus?.toLowerCase() === 'agent-arrived-restaurant' ? "flex-start" : "center" }}>
                                            <Typography
                                                variant="body2"
                                                component="div"
                                                sx={{
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    bgcolor: "#e6f4ea",
                                                    color: green[800],
                                                    px: 1.2,
                                                    py: 0.25,
                                                    borderRadius: 1,
                                                    display: "inline-block",
                                                    lineHeight: 1.2,
                                                    // Fixed width only for "Agent Arrived Restaurant" to ensure 2-line display
                                                    width: currentOrder.orderStatus?.toLowerCase() === 'agent-arrived-restaurant' ? "85px" : "fit-content",
                                                    whiteSpace: currentOrder.orderStatus?.toLowerCase() === 'agent-arrived-restaurant' ? "pre-line" : "nowrap",
                                                    wordBreak: "keep-all", // Prevent breaking within words
                                                    textAlign: currentOrder.orderStatus?.toLowerCase() === 'agent-arrived-restaurant' ? "center" : "left",
                                                }}
                                            >
                                                {formatStatusWithLineBreak(currentOrder.orderStatus)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Call Button - Same for all statuses */}
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<CallIcon fontSize="small" sx={{ color: green[600] }} />}
                                        disabled={callingAgent || !currentOrder.agentDetails?.phone || currentOrder.agentDetails.phone === 'N/A' || !profilePhoneNumber || !currentOrder.order_code}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: 1,
                                            fontSize: 12,
                                            color: green[600],
                                            borderColor: green[600],
                                            "&:hover": { borderColor: green[800], color: green[800] },
                                            "&:disabled": { opacity: 0.5 },
                                            px: 1.5,
                                        }}
                                        onClick={async () => {
                                            const agentPhone = currentOrder.agentDetails?.phone;
                                            if (!agentPhone || agentPhone === "N/A" || !profilePhoneNumber || !currentOrder.order_code) {
                                                notifyError("Phone number or order code not available");
                                                return;
                                            }

                                            setCallingAgent(true);
                                            try {
                                                const payload = {
                                                    order_id: currentOrder.order_code,
                                                    from: profilePhoneNumber,
                                                    to: agentPhone,
                                                };
                                                const response = await initiateExotelCall(payload);
                                                if (response?.status === 200 || response?.status === 201) {
                                                    setShowAgentCallInitiatedModal(true);
                                                } else {
                                                    const errorMessage = response?.data?.message || "Failed to initiate call";
                                                    notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
                                                }
                                            } catch (error: any) {
                                                const errorMessage = error?.response?.data?.message || "Failed to initiate call";
                                                notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
                                            } finally {
                                                setCallingAgent(false);
                                            }
                                        }}
                                    >
                                        Call
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    p: 2,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    No Delivery Partner Available
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>
            <CallInitiatedModal
                open={showCallInitiatedModal}
                onClose={() => setShowCallInitiatedModal(false)}
                phoneNumber={profilePhoneNumber}
                recipientType="customer"
            />
            <CallInitiatedModal
                open={showAgentCallInitiatedModal}
                onClose={() => setShowAgentCallInitiatedModal(false)}
                phoneNumber={profilePhoneNumber}
                recipientType="agent"
            />
      {isTazty && (
        <PrintOrderModal
          open={printModalOpen}
          onClose={() => setPrintModalOpen(false)}
          order={order}
        />
      )}
        </>
    );
};

// Helper to normalize invoice shape to expected order-like shape for UI
const mapInvoiceToOrderLike = (inv: any) => {
  const dateStr = inv?.updated_at || inv?.invoice_date || inv?.order_date || "";
  const customerName = inv?.customer_name || inv?.customer?.name || "N/A";
  const customerPhone = inv?.customer_phone || inv?.customer?.phone || "N/A";
    const billing = inv?.billing_address || inv?.customer?.billing_address || {};
    // const address1 = typeof billing === 'string' ? billing : billing?.address1;
    // const address2 = typeof billing === 'string' ? '' : billing?.address2;
  const address3 = typeof billing === "string" ? "" : billing?.address3;
  const city = typeof billing === "string" ? "" : billing?.city;
  const state = typeof billing === "string" ? "" : billing?.state;
  const pincode = typeof billing === "string" ? "" : billing?.pincode;
  const rawItems =
    inv?.invoice_items ||
    inv?.items ||
    inv?.order_details ||
    inv?.order_items ||
    [];
  const order_items = (Array.isArray(rawItems) ? rawItems : []).map(
    (it: any) => ({
      product_id: it.product_id || it?.product?.id || it?.id || "",
      product_name: it.product_name || it.name || it?.product?.name || "Item",
        product_type: it.product_type,
        quantity: it.quantity ?? it.qty ?? 1,
        price: String(it.price ?? it.amount ?? 0),
        is_veg: it.is_veg,
        addons: it.addons,
      total_price: String(it.total_price ?? it.amount ?? 0),
    }),
  );
    return {
        id: inv?.id,
        order_id: inv?.order_id,
        order_code: inv?.order_code,
        updated_at: dateStr,
        customer: {
            name: customerName,
            phone: customerPhone,
            order_count: inv?.customer?.order_count,
            billing_address: {
                // address1,
                // address2,
                address3,
                city,
                state,
        pincode,
      },
        },
        total_amount: String(inv?.total_amount ?? inv?.amount ?? inv?.total ?? 0),
        payment_method: inv?.payment_method,
    instruction:
      inv?.instructions || inv?.instruction || "No special instructions",
    instructions:
      inv?.instructions || inv?.instruction || "No special instructions",
        cutlery_instruction: inv?.cutlery_instruction,
        order_items,
        agent_details: inv?.agent_details || null,
        delivery_time_estimate: inv?.delivery_time_estimate,
        status: inv?.status,
        cgst: inv?.cgst,
        sgst: inv?.sgst,
        igst: inv?.igst,
        shipping_charge: inv?.shipping_charge,
        preparation_time: inv?.preparation_time,
    sybTotalAmount: inv?.sub_total
      ? parseFloat(inv?.sub_total).toFixed(2)
      : undefined,
    store: inv?.store,
    pickup_code: inv?.pickup_code ?? inv?.pickupCode ?? null,
    delivery_code: inv?.delivery_code ?? inv?.deliveryCode ?? null,
    };
};

// Main Preparing Component
const Preparing: React.FC = () => {
    const [invoiceData, setInvoiceData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [fetchingMore, setFetchingMore] = useState(false);
    const { store_id } = useParams();
  const observer = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

    // Get the context for sharing counts and auto-refresh state
    const orderCountsContext = useContext(OrderCountsContext);
  const { updateCount, countsLoaded, refreshCallbacksRef } =
    orderCountsContext || {
      updateCount: () => {},
        countsLoaded: false,
      refreshCallbacksRef: {
        current: {} as { preparing?: () => void; ready?: () => void },
      },
    };

  const fetchInvoices = useCallback(
    async (pageToFetch = 1, append = false, showLoading = true) => {
        if (pageToFetch > totalPages) return;
        if (pageToFetch === 1 && showLoading) setLoading(true);
        else if (pageToFetch > 1) setFetchingMore(true);
        setError(null);
        try {
        const res = await getPreparingOrders(
          pageToFetch,
          store_id as any,
          "billed",
          showLoading,
        );
            const data = res?.data?.data;
            const invoices = data?.invoice || data?.orders || data || [];
            setTotalPages(data?.totalPages || 1);
            const newTotalCount = data?.total || invoices.length;
            setTotalCount(newTotalCount);
            // Update the context with the new count (only if counts haven't been loaded yet)
            if (!countsLoaded) {
          updateCount("preparing", newTotalCount);
            }
            if (append) {
          setInvoiceData((prev) => [
            ...prev,
            ...(Array.isArray(invoices) ? invoices : []),
          ]);
            } else {
                setInvoiceData(Array.isArray(invoices) ? invoices : []);
            }
        } catch (e: any) {
            setError(e?.message || "Failed to fetch invoices");
            if (!append) setInvoiceData([]);
        } finally {
            if (pageToFetch === 1 && showLoading) setLoading(false);
            else if (pageToFetch > 1) setFetchingMore(false);
        }
    },
    [store_id, totalPages, countsLoaded, updateCount],
  );

    useEffect(() => {
        setPage(1);
        fetchInvoices(1, false);
    }, [store_id]);

    // Get activeKey from Redux to check if we're on Preparing tab
    const activeKey = useAppSelector((state: any) => state.tab?.activekey);

    // Register refresh callback with parent component
    useEffect(() => {
        if (refreshCallbacksRef && refreshCallbacksRef.current) {
            refreshCallbacksRef.current.preparing = () => {
        console.log("Auto-refreshing Preparing tab...");
                fetchInvoices(1, false, false); // showLoading = false
            };
        }
        return () => {
            if (refreshCallbacksRef && refreshCallbacksRef.current) {
                delete refreshCallbacksRef.current.preparing;
            }
        };
    }, [fetchInvoices, refreshCallbacksRef]);

    // Listen for order acceptance events from FCM popup to auto-refresh
    useEffect(() => {
        const handleOrderAccepted = () => {
      // Only refresh if we're currently on the Preparing tab
      if (activeKey === "preparing") {
        console.log("Order accepted from FCM, refreshing Preparing tab...");
        // Small delay to ensure server has processed the order
                setTimeout(() => {
                    fetchInvoices(1, false);
                }, 500);
            }
        };

    window.addEventListener("orderAccepted", handleOrderAccepted);
        return () => {
      window.removeEventListener("orderAccepted", handleOrderAccepted);
        };
    }, [activeKey, fetchInvoices]);

  // Infinite scroll observer
  useEffect(() => {
    if (fetchingMore || loading) return;
    if (!sentinelRef.current) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new window.IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && page < totalPages) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchInvoices(nextPage, true);
      }
    });
    observer.current.observe(sentinelRef.current);
    return () => observer.current?.disconnect();
  }, [fetchingMore, loading, page, totalPages, fetchInvoices]);

    // Filter for 'billed', 'agent-assigned', and 'agent-arrived-restaurant' statuses
  const preparingStatuses = [
    "billed",
    "agent-assigned",
    "agent-arrived-restaurant",
  ];
    const preparingInvoices = Array.isArray(invoiceData) 
        ? invoiceData.filter((inv: any) => {
            // Check status from multiple possible fields (status might be in different fields)
            const invStatus = (
                inv?.status || 
                inv?.invoice_status || 
                inv?.order_status || 
                inv?.current_status ||
          ""
        )
          .toString()
          .toLowerCase()
          .trim();
            
            return preparingStatuses.includes(invStatus);
        }) 
        : [];
    
    // Use all preparing invoices (no filtering based on items - invoices should always be displayed)
    const validBilled = preparingInvoices;
    
    // Deduplicate by order_id: keep only one invoice per order_id, preferring the one with more items
    const orderIdMap = new Map<number | string, any>();
    validBilled.forEach((inv: any) => {
        const orderId = inv?.order_id;
        if (!orderId) return;
        
        const existing = orderIdMap.get(orderId);
        if (!existing) {
            orderIdMap.set(orderId, inv);
        } else {
            // Compare items count and total amount
      const existingItems =
        existing?.invoice_items ||
        existing?.items ||
        existing?.order_details ||
        existing?.order_items ||
        [];
      const existingItemsCount = Array.isArray(existingItems)
        ? existingItems.length
        : 0;
      const existingTotal = parseFloat(
        existing?.total_amount || existing?.amount || existing?.total || 0,
      );
            
      const currentItems =
        inv?.invoice_items ||
        inv?.items ||
        inv?.order_details ||
        inv?.order_items ||
        [];
      const currentItemsCount = Array.isArray(currentItems)
        ? currentItems.length
        : 0;
      const currentTotal = parseFloat(
        inv?.total_amount || inv?.amount || inv?.total || 0,
      );
            
            // Keep the invoice with more items, or if same items, keep the one with higher total
      if (
        currentItemsCount > existingItemsCount ||
        (currentItemsCount === existingItemsCount &&
          currentTotal > existingTotal)
      ) {
                orderIdMap.set(orderId, inv);
            }
        }
    });
    
    const deduplicatedBilled = Array.from(orderIdMap.values());
  // console.log( "deduplicatedBilled", deduplicatedBilled);
    const normalized = deduplicatedBilled.map(mapInvoiceToOrderLike);
  // console.log( "normalized", normalized);
    return (
        <Box>
            {loading ? (
                <Typography>Loading...</Typography>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : normalized.length > 0 ? (
                <>
                    {normalized.map((order, index) => (
                        <SinglePreparingOrderBox
                            key={order.id || index}
                            order={order}
                            onOrderReady={() => { }}
                            fetchInvoiceData={fetchInvoices}
                        />
                    ))}
                    {page < totalPages && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={fetchingMore ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}
                                disabled={fetchingMore}
                                onClick={() => {
                                    const nextPage = page + 1;
                                    setPage(nextPage);
                                    fetchInvoices(nextPage, true, false);
                                }}
                                sx={{ textTransform: 'none' }}
                            >
                                {fetchingMore ? 'Loading...' : 'View More'}
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "auto",
                        p: 2,
                    }}
                >
                    <img
                        src="path/to/your/preparing.png"
                        alt=""
                        style={{
                            maxWidth: "100%",
                            height: "auto",
                            display: "block",
                            marginBottom: "16px",
                        }}
                    />
                    <Typography variant="body1" color="text.secondary">
                        No orders in preparing stage
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default Preparing;                                                           